import { composeClassNames } from "@faedev/editor-utils";
import { useRouter } from "next/router";
import queryString from "query-string";
import { useEffect, useState } from "react";
import { Alert } from "react-bootstrap";
import ReactGA from "react-ga4";
import Translate from "src/components/Translate";
import constants from "src/constants";
import getCookieByName from "src/functions/getCookieByName";
import validateAuthToken from "src/functions/validateAuthToken";
import { sAlert } from "src/styles/sAlert.module.scss";
import { VariantColor } from "src/types/VariantColor";
import Preloader from "./Preloader";

type Props = {
  setPayuCheckReady?: (setPayuCheckReady: boolean) => void;
};

const setGtmDataLayerPurchase = (data: any) => {
  if (data !== null) {
    const coupons = data?.order_items?.reduce(
      (a, o) => (o.entity_type === "coupon" && a.push(o.title), a),
      []
    );
    const coupon =
      coupons && coupons.length > 0
        ? String(coupons[0]).split(" ").pop().trim()
        : "";
    const orderItems =
      data &&
      data.order_items.length > 0 &&
      data.order_items.map((item) => {
        const titleArr = item?.title?.includes(": ")
          ? item?.title?.split(": ")
          : [];
        const discount =
          item?.price_basic && item?.price && item.price_basic > item.price
            ? item.price_basic - item.price
            : 0;
        return {
          item_id: item.id,
          item_name: titleArr.length > 1 ? titleArr[1] : item?.title,
          currency: data?.currency?.toUpperCase(),
          discount: discount,
          item_brand: titleArr.length > 1 ? titleArr[0] : constants.Brand.name,
          item_category: item?.entity_type, // lecture | program | subscription
          item_variant: item?.entity_subtype, // lecture | stream | program | festival | yearly | monthly
          location_id: constants.Locations.czechPlaceId,
          price: data?.price,
          quantity: 1,
        };
      });
    ReactGA.initialize(constants.Marketing.gtmId);
    ReactGA.gtag("event", "purchase", {
      transaction_id: data?.id,
      value: data?.price,
      tax: data?.vatMode === "basic" ? 0 : 0,
      shipping: 0,
      currency: data?.currency?.toUpperCase(),
      coupon: coupon,
      items: orderItems,
    });
  }
};

function PayuCheck({ setPayuCheckReady }: Props) {
  const { push, replace } = useRouter();
  const [alertMessage, setAlertMessage] = useState("");
  const [alertMessageStatus, setAlertMessageStatus] = useState<VariantColor>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const timeId = setTimeout(() => {
      // After 10 seconds set the show value to false
      setAlertMessage("");
      setAlertMessageStatus(null);
      setPayuCheckReady(false);
    }, 10000);

    return () => {
      clearTimeout(timeId);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertMessage]);

  // redirect from payu
  useEffect(() => {
    let payuCheckHash = queryString.parse(location.hash);
    const payuCheckIdHash =
      typeof payuCheckHash === "object"
        ? payuCheckHash["payments?payu-rqt"]
        : undefined;

    let payuCheck = queryString.parse(location.search);
    const payuCheckId =
      typeof payuCheck === "object" ? payuCheck["payu-rqt"] : undefined;

    if (payuCheckId !== undefined || payuCheckIdHash !== undefined) {
      setLoading(true);

      // TO TEST FAIL: fetch(`${constants.Paths.apiUrl}/payu-check/${payuCheckId}?fail`, {
      // CORRECT WAY: fetch(`${constants.Paths.apiUrl}/payu-check/${payuCheckId}`, {
      fetch(
        `${constants.Paths.apiUrl}/payu-check/${
          payuCheckId !== undefined ? payuCheckId : payuCheckIdHash
        }`,
        {
          method: "GET",
          headers: validateAuthToken(getCookieByName(`auth-token`)),
        }
      ).then(async (res) => {
        if (res.ok) {
          let response = await res.json();
          switch (response.status) {
            case "done":
              setAlertMessage("Payment was successful!");
              setAlertMessageStatus("success");
              setGtmDataLayerPurchase(response.order);
              const newURL = location.href.split("?")[0];
              replace(newURL, undefined, { shallow: true });
              break;
            case "pending":
              setAlertMessage("Payment awaiting...");
              setAlertMessageStatus("warning");
              break;
            case "canceled":
              push(`${constants.Paths.pageCart}?payment-failed`);
              break;
            case "paid-unfinished":
              setAlertMessage(
                "Payment was successful, we are processing the order."
              );
              setAlertMessageStatus("warning");
              break;
            default:
              setAlertMessage(
                "An unexpected error occurred, please contact support."
              );
              setAlertMessageStatus("danger");
              setPayuCheckReady(true);
          }
          setPayuCheckReady(true);
          setLoading(false);
        } else {
          setAlertMessage(
            "An unexpected error occurred, please contact support."
          );
          setAlertMessageStatus("danger");
          setPayuCheckReady(true);
          setLoading(false);
        }
      });
    } else {
      setPayuCheckReady(false);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {loading && (
        <Alert
          variant={"warning"}
          className={composeClassNames([sAlert, "alert-flash"])}
        >
          <Preloader size="sm" /> <Translate tKey={"Payment verification"} />
        </Alert>
      )}
      {alertMessage && (
        <Alert
          variant={alertMessageStatus}
          className={composeClassNames([sAlert, "alert-flash"])}
        >
          <Translate tKey={alertMessage} />
        </Alert>
      )}
    </>
  );
}

export default PayuCheck;
